import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useFormService } from '../core/form-service';
import { FORM_STATES, FORM_EVENTS } from '../machine/machine-config';
import { isPromise } from './is-promise';

export const RepositoryFetcher = ({ isRepositoryFetched, setIsRepositoryFetched }) => {
  const [chunkError, setChunkError] = useState(null);
  const { state, send } = useFormService();
  const config = _.get(state, ['context', 'config']);
  const isFetching = state.matches(FORM_STATES.FETCHING);
  const [isFactoryResolved, setIsFactoryResolved] = useState(false);
  const { repository } = config;
  const lazyComponent = useRef(null);

  useEffect(() => {
    if (!isRepositoryFetched && isFetching && !isFactoryResolved) {
      let repositoryPromise;

      if (_.isFunction(repository)) {
        try {
          repositoryPromise = repository();
        } catch (e) {
          // if error is thrown, then repository could already be loaded
        }
      }

      if (!repositoryPromise || !isPromise(repositoryPromise)) {
        setIsRepositoryFetched(true);
        return;
      }

      if (isPromise(repositoryPromise)) {
        repositoryPromise.then((resolvedConfig) => {
          lazyComponent.current = resolvedConfig;
          setIsFactoryResolved(true);
          send(FORM_EVENTS.UPDATE_CONFIG, { config: { repositoryWrapper: resolvedConfig } });
        }).catch((e) => {
          if (e && e.name === 'ChunkLoadError') {
            setChunkError(e);
          }
        });
      }
    }
  }, [isFactoryResolved, isFetching, isRepositoryFetched, repository, send, setIsRepositoryFetched]);

  useEffect(() => {
    if (chunkError) {
      throw chunkError;
    }
  }, [chunkError]);

  const updateRepositoryCallback = useCallback((callbackRepository) => {
    if (!isRepositoryFetched) {
      setIsRepositoryFetched(true);
      send(FORM_EVENTS.UPDATE_CONFIG, { config: { repository: callbackRepository } });
    }
  }, [isRepositoryFetched, send, setIsRepositoryFetched]);

  return (
    lazyComponent.current ? <lazyComponent.current callback={updateRepositoryCallback} /> : null
  );
};

RepositoryFetcher.propTypes = {
  isRepositoryFetched: PropTypes.bool.isRequired,
  setIsRepositoryFetched: PropTypes.func.isRequired,
};
